import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Configure axios once so cookies are always included
axios.defaults.withCredentials = true;

// Attach Bearer token as a defence-in-depth alongside the httpOnly cookie
// (helps in cross-site iframe/dev environments where the cookie may be blocked)
const TOKEN_KEY = 'cmc_admin_token';
axios.interceptors.request.use((config) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t && config.url && config.url.startsWith(API_BASE)) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${t}`;
    }
  }
  return config;
});

const AuthContext = createContext({
  status: 'loading',
  user: null,
  login: async () => {},
  logout: async () => {},
});

export const AuthProvider = ({ children }) => {
  const [status, setStatus] = useState('loading'); // loading | authenticated | anonymous
  const [user, setUser] = useState(null);

  const checkSession = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/api/auth/me`);
      setUser(data);
      setStatus('authenticated');
    } catch (e) {
      if (e?.response?.status && e.response.status !== 401) {
        console.warn('[auth] session check failed', e);
      }
      setUser(null);
      setStatus('anonymous');
    }
  }, []);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API_BASE}/api/auth/login`, { email, password });
    if (data?.token) {
      localStorage.setItem(TOKEN_KEY, data.token);
    }
    setUser(data.user);
    setStatus('authenticated');
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API_BASE}/api/auth/logout`);
    } catch (e) {
      console.warn('[auth] logout request failed; clearing client state anyway', e);
    }
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    setStatus('anonymous');
  }, []);

  const value = useMemo(
    () => ({ status, user, login, logout, refresh: checkSession }),
    [status, user, login, logout, checkSession],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

export function formatApiErrorDetail(detail) {
  if (detail == null) return null;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}
